
import { useState, useEffect } from 'react';
import { useForm, UseFormProps, UseFormReturn, DefaultValues, FieldValues } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

// Extend UseFormReturn to include our custom properties
export interface FormWithPersistenceReturn<TFieldValues extends FieldValues = FieldValues, TContext = any> 
  extends UseFormReturn<TFieldValues, TContext> {
  isRestoringData: boolean;
}

export const useFormWithPersistence = <
  TFieldValues extends Record<string, any> = Record<string, any>,
  TContext = any
>(
  schema: yup.ObjectSchema<any>,
  storageKey: string,
  defaultValues?: DefaultValues<TFieldValues>,
): FormWithPersistenceReturn<TFieldValues, TContext> => {
  const [isRestoringData, setIsRestoringData] = useState(false);
  const [isReady, setIsReady] = useState(false);

  // Set up form with validation mode onBlur and onChange for immediate validation feedback
  const formMethods = useForm<TFieldValues, TContext>({
    resolver: yupResolver(schema),
    defaultValues: defaultValues,
    mode: 'all', // This enables validation as user types and when fields lose focus
    reValidateMode: 'onChange', // Re-validate on every change
  });

  // Load saved form data on initial render
  useEffect(() => {
    try {
      setIsRestoringData(true);
      const savedForm = localStorage.getItem(storageKey);
      if (savedForm) {
        const parsedForm = JSON.parse(savedForm);
        
        // Reset form with saved values
        formMethods.reset(parsedForm as TFieldValues);
      }
    } catch (error) {
      console.error('Error restoring form data:', error);
    } finally {
      setIsRestoringData(false);
      setIsReady(true);
    }
  }, [storageKey]);

  // Save form data on change
  useEffect(() => {
    if (!isReady) return;

    const subscription = formMethods.watch((formValues) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(formValues));
      } catch (error) {
        console.error('Error saving form data:', error);
      }
    });

    return () => subscription.unsubscribe();
  }, [formMethods, storageKey, isReady]);

  // Return the form methods with the additional isRestoringData property
  return {
    ...formMethods,
    isRestoringData,
  };
};
